/**
 * Skeleton shimmer components.
 *
 * Every page gets a structured skeleton that mirrors its actual layout so the user sees
 * where each section will appear while data is loading, instead of a generic spinner that
 * communicates nothing about what is coming.
 */

import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** A single animated bar whose width approximates the real content. */
export function SkeletonLine({ width = "100%" }: { width?: string }) {
  return <span className="skeleton-line" style={{ width }} />;
}

/** Several lines of mock text, useful for card bodies and descriptions. */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  const widths = ["100%", "92%", "78%", "85%", "60%", "95%", "70%"];
  return (
    <div className="skeleton-text">
      {Array.from({ length: lines }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
        <SkeletonLine key={i} width={widths[i % widths.length] ?? "100%"} />
      ))}
    </div>
  );
}

/** A rectangular block, used for chart areas and image placeholders. */
export function SkeletonBlock({ height = "12rem" }: { height?: string }) {
  return <div className="skeleton-block" style={{ height }} />;
}

// ---------------------------------------------------------------------------
// Composites
// ---------------------------------------------------------------------------

/** A page header with a shimmering title and description line. */
export function SkeletonPageHeader() {
  return (
    <header className="page-header">
      <div className="skeleton-text">
        <SkeletonLine width="14rem" />
        <SkeletonLine width="28rem" />
      </div>
    </header>
  );
}

/** A card with a shimmering header and body content. */
export function SkeletonCard({
  children,
  className = "",
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card skeleton-card ${className}`}>
      {children ?? (
        <>
          <div className="card-header">
            <div className="skeleton-text">
              <SkeletonLine width="10rem" />
              <SkeletonLine width="22rem" />
            </div>
          </div>
          <SkeletonText lines={3} />
        </>
      )}
    </section>
  );
}

/** A metric card skeleton matching the Metric component layout. */
export function SkeletonMetric() {
  return (
    <section className="card metric">
      <SkeletonLine width="6rem" />
      <SkeletonLine width="4rem" />
      <SkeletonLine width="9rem" />
    </section>
  );
}

/** A row of filter fields matching the filters layout. */
export function SkeletonFilters({ count = 4, bare = false }: { count?: number; bare?: boolean }) {
  const fields = (
    <div className="filters">
      {Array.from({ length: count }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
        <div className="field" key={i}>
          <SkeletonLine width="5rem" />
          <div className="skeleton-input" />
        </div>
      ))}
    </div>
  );
  return bare ? fields : <section className="card">{fields}</section>;
}

/**
 * A table skeleton with a header row and N shimmer rows.
 * When `bare` is true the card wrapper is omitted so it can sit inside an existing Card.
 */
export function SkeletonTable({
  columns = 5,
  rows = 6,
  bare = false,
}: {
  columns?: number;
  rows?: number;
  bare?: boolean;
}) {
  const colWidths = ["35%", "18%", "22%", "15%", "10%", "12%", "20%", "16%"];
  const content = (
    <>
      {!bare && (
        <div className="card-header">
          <div className="skeleton-text">
            <SkeletonLine width="9rem" />
            <SkeletonLine width="14rem" />
          </div>
        </div>
      )}
      <div className="skeleton-table" role="presentation">
        <div className="skeleton-table-header">
          {Array.from({ length: columns }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
            <SkeletonLine key={i} width={colWidths[i % colWidths.length] ?? "15%"} />
          ))}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
          <div className="skeleton-table-row" key={r}>
            {Array.from({ length: columns }, (_, c) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
              <SkeletonLine key={c} width={colWidths[c % colWidths.length] ?? "15%"} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
  return bare ? content : <section className="card">{content}</section>;
}

/**
 * Full-surface placeholder for application boundary checks: session, workspace
 * access, and invitation previews that gate an entire route.
 */
export function BoundarySkeleton() {
  return (
    <div className="boundary-skeleton" role="status" aria-label="Loading">
      <section className="card skeleton-card boundary-skeleton-card">
        <SkeletonLine width="12rem" />
        <SkeletonText lines={2} />
      </section>
    </div>
  );
}

/**
 * Full-page placeholder mirroring the signed-in application frame: the sidebar with its
 * product row, organisation row, navigation groups and profile footer, then the topbar and an opening
 * page scaffold. Shown instead of the boundary card when a previous visit left a
 * session hint behind, so a returning user watches the familiar room assemble
 * rather than a lone card that snaps into a completely different layout.
 *
 * Everything here is presentational: the real layout classes carry the geometry,
 * while divs and spans stand in for the shell's landmarks so no fake navigation or
 * main region is announced while loading.
 */
export function AppShellSkeleton() {
  const navGroups = [3, 3, 4, 2];
  return (
    <div className="app-layout app-skeleton" role="status" aria-label="Loading workspace">
      <div className="sidebar" aria-hidden="true">
        <div className="sidebar-brand">
          <span className="skeleton-line app-skeleton-brand-mark" />
          <SkeletonLine width="6.5rem" />
        </div>
        <div className="sidebar-tenant">
          <span className="skeleton-line app-skeleton-monogram" />
          <div className="skeleton-text">
            <SkeletonLine width="8rem" />
            <SkeletonLine width="5.5rem" />
          </div>
        </div>
        <div className="sidebar-nav">
          {navGroups.map((rows, group) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
            <div className="sidebar-nav-group" key={group}>
              <span className="sidebar-nav-heading">
                <SkeletonLine width="3.5rem" />
              </span>
              {Array.from({ length: rows }, (_, item) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
                <span className="app-skeleton-nav-item" key={item}>
                  <span className="skeleton-line app-skeleton-icon" />
                  <SkeletonLine width={item % 2 === 0 ? "5.5rem" : "7rem"} />
                </span>
              ))}
            </div>
          ))}
        </div>
        <div className="sidebar-footer">
          <div className="app-skeleton-profile">
            <span className="skeleton-line skeleton-avatar" />
            <div className="skeleton-text">
              <SkeletonLine width="7rem" />
              <SkeletonLine width="5rem" />
            </div>
          </div>
        </div>
      </div>
      <div className="main-column">
        <div className="topbar">
          <div className="topbar-context skeleton-text">
            <SkeletonLine width="10rem" />
            <SkeletonLine width="6.5rem" />
          </div>
        </div>
        <div className="main-content">
          <div className="page-header">
            <div className="skeleton-text">
              <SkeletonLine width="14rem" />
              <SkeletonLine width="26rem" />
            </div>
          </div>
          <div className="metrics-grid">
            <SkeletonMetric />
            <SkeletonMetric />
            <SkeletonMetric />
          </div>
          <div className="content-grid">
            <SkeletonCard className="span-7" />
            <SkeletonCard className="span-5" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Skeleton for settings and integration panels: stacked cards, optionally ending in a table. */
export function PanelSkeleton({ table = true }: { table?: boolean }) {
  return (
    <div className="stack">
      <SkeletonCard />
      {table ? <SkeletonTable /> : <SkeletonCard />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page-specific skeletons
// ---------------------------------------------------------------------------

/** Skeleton for list pages: Bookings, Events, Contacts, Vendors, etc. */
export function ListPageSkeleton({
  title,
  description,
  filterCount = 4,
  columnCount = 5,
  rowCount = 8,
}: {
  title: string;
  description: string;
  filterCount?: number;
  columnCount?: number;
  rowCount?: number;
}) {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
      </header>
      <SkeletonFilters count={filterCount} />
      <SkeletonTable columns={columnCount} rows={rowCount} />
    </>
  );
}

/** Skeleton for dashboard home pages: metrics + upcoming card + recent card. */
export function DashboardSkeleton({ title }: { title: string }) {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          <SkeletonLine width="22rem" />
        </div>
      </header>
      <div className="metrics-grid">
        <SkeletonMetric />
        <SkeletonMetric />
        <SkeletonMetric />
      </div>
      <div className="content-grid">
        <SkeletonCard className="span-7" />
        <SkeletonCard className="span-5" />
      </div>
    </>
  );
}

/** Skeleton for detail/editor pages: hero card + sidebar cards. */
export function DetailPageSkeleton({
  title,
  backTo,
  backLabel,
}: {
  title: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <section className="editor-page">
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          <SkeletonLine width="16rem" />
        </div>
        <div className="page-actions">
          <a className="button secondary" href={backTo}>
            {backLabel}
          </a>
        </div>
      </header>
      <SkeletonCard>
        <div className="card-header">
          <div className="skeleton-text">
            <SkeletonLine width="12rem" />
            <SkeletonLine width="30rem" />
          </div>
        </div>
        <div className="skeleton-strip">
          <div>
            <SkeletonLine width="3rem" />
            <SkeletonLine width="10rem" />
          </div>
          <div>
            <SkeletonLine width="3rem" />
            <SkeletonLine width="10rem" />
          </div>
          <div>
            <SkeletonLine width="4rem" />
            <SkeletonLine width="8rem" />
          </div>
          <div>
            <SkeletonLine width="5rem" />
            <SkeletonLine width="4rem" />
          </div>
        </div>
      </SkeletonCard>
      <div className="content-grid">
        <SkeletonCard className="span-7" />
        <SkeletonCard className="span-5" />
      </div>
    </section>
  );
}

/** Skeleton for focused editor pages: header + one large form card. */
export function EditorPageSkeleton({ fieldCount = 8 }: { fieldCount?: number }) {
  return (
    <section className="editor-page">
      <header className="page-header">
        <div className="skeleton-text">
          <SkeletonLine width="14rem" />
          <SkeletonLine width="24rem" />
        </div>
      </header>
      <SkeletonFilters count={fieldCount} />
    </section>
  );
}

/** Skeleton for the calendar page: toolbar + a viewport-height calendar grid. */
export function CalendarSkeleton() {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>Calendar</h1>
          <SkeletonLine width="20rem" />
        </div>
      </header>
      <div className="skeleton-tabs">
        <SkeletonLine width="9rem" />
        <SkeletonLine width="16rem" />
        <SkeletonLine width="10rem" />
      </div>
      <SkeletonCard>
        <SkeletonBlock height="min(55vh, 34rem)" />
      </SkeletonCard>
    </>
  );
}

/** Skeleton for the analytics page: filters + metrics + chart + venue breakdown. */
export function AnalyticsSkeleton() {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>Analytics</h1>
          <p>Performance across your venues, rentals, and commercial pipeline.</p>
        </div>
      </header>
      <SkeletonFilters count={3} />
      <div className="metrics-grid">
        <SkeletonMetric />
        <SkeletonMetric />
        <SkeletonMetric />
        <SkeletonMetric />
      </div>
      <div className="content-grid">
        <SkeletonCard className="span-7">
          <div className="card-header">
            <div>
              <SkeletonLine width="8rem" />
            </div>
          </div>
          <SkeletonBlock height="14rem" />
        </SkeletonCard>
        <SkeletonCard className="span-5" />
      </div>
    </>
  );
}

/** Skeleton for finance: metrics + venue filter + tabs + invoices table. */
export function FinanceSkeleton() {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>Finance</h1>
          <p>Invoices, payments, quotes, and reconciliation across your venues.</p>
        </div>
      </header>
      <div className="metrics-grid">
        <SkeletonMetric />
        <SkeletonMetric />
        <SkeletonMetric />
      </div>
      <SkeletonFilters count={1} />
      <div className="skeleton-tabs">
        <SkeletonLine width="5rem" />
        <SkeletonLine width="4rem" />
        <SkeletonLine width="6rem" />
        <SkeletonLine width="7rem" />
      </div>
      <SkeletonTable columns={6} rows={8} />
    </>
  );
}

/** Skeleton for inventory: header + per-venue items table. */
export function InventorySkeleton() {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>Inventory</h1>
          <SkeletonLine width="24rem" />
        </div>
      </header>
      <SkeletonTable columns={5} rows={6} />
    </>
  );
}

/** Skeleton for venues: venue cards grid. */
export function VenuesSkeleton() {
  return (
    <>
      <header className="page-header">
        <div>
          <h1>Venues</h1>
          <p>Your properties and the locations inside them.</p>
        </div>
      </header>
      <div className="content-grid">
        <SkeletonCard className="span-6" />
        <SkeletonCard className="span-6" />
        <SkeletonCard className="span-6" />
      </div>
    </>
  );
}
